/**
 * The per-lap speed-vs-distance series the consistency statistics bin over
 * (curve %, chart divergence band, map gradient — all via binSpeedSpreads).
 *
 * Pass 29: the statistics must read the SAME recalibrated distance frame the
 * zone chart plots. On the raw odometer frame, cumulative drift shifts each
 * lap's curve by metres — which either manufactures phantom cross-lap
 * deviation the chart never shows (dense data), or drops a drifted lap into
 * bins no other lap occupies so it silently stops being measured (sparse
 * data). Callers therefore build traces from recalibrated points wherever a
 * recalibrated frame exists; `tracesFromRawLaps` remains only as the
 * wholesale fallback when recalibration itself fails.
 */
import type { Lap } from '../../../../domain/lap/Lap'
import type { RecalibratedLap } from '../recalibratedLapPoints'

export interface SpeedTracePoint {
  /** Absolute course distance (m) — recalibrated where the source frame is. */
  distance: number
  /** Speed in km/h. */
  speed: number
  lat?: number
  lng?: number
}

/** One lap's speed trace on the frame the consistency stats bin over. */
export interface SpeedTrace {
  /** Domain lap id — matches `Lap.id` / `excludedLapIds` entries. */
  id: string
  lapNumber: number
  points: SpeedTracePoint[]
}

/** The honest frame: the exact points the zone chart plots. */
export function tracesFromRecalibratedLaps(
  laps: RecalibratedLap[]
): SpeedTrace[] {
  return laps.map((lap) => ({
    id: lap.lapId,
    lapNumber: lap.lapNumber,
    points: lap.points.map((p) => ({
      distance: p.distance,
      speed: p.speed,
      lat: p.lat,
      lng: p.lng,
    })),
  }))
}

/**
 * Raw odometer-frame fallback (pre-pass-29 source: spatial buckets, else GPS
 * points). Only for when recalibration throws — never the primary path.
 */
export function tracesFromRawLaps(laps: Lap[]): SpeedTrace[] {
  return laps.map((lap) => {
    const buckets = lap.spatialBuckets
    if (buckets && buckets.length > 0) {
      const gps = lap.getGpsPoints()
      return {
        id: lap.id,
        lapNumber: lap.lapNumber,
        points: buckets.map((b) => ({
          distance: b.distance,
          speed: b.speedKmh,
          lat: gps[b.gpsIndex]?.latitude,
          lng: gps[b.gpsIndex]?.longitude,
        })),
      }
    }
    return {
      id: lap.id,
      lapNumber: lap.lapNumber,
      points: lap.getGpsPoints().map((p) => ({
        distance: p.distanceMeters,
        speed: p.speedKmh,
        lat: p.latitude,
        lng: p.longitude,
      })),
    }
  })
}

/** Structural shape of a chart telemetry lap (mobile's LapTelemetryData):
 *  dataPoints already sit on the recalibrated comparison frame. */
export interface ChartTelemetryLapLike {
  lapId: string
  lapNumber: number
  dataPoints: { distance: number; speed: number; lat?: number; lng?: number }[]
}

/** Adapter for clients whose chart data is already recalibrated (mobile's
 *  useLapComparison output) — zero extra recalibration cost. */
export function tracesFromChartTelemetry(
  laps: ChartTelemetryLapLike[]
): SpeedTrace[] {
  return laps.map((lap) => ({
    id: lap.lapId,
    lapNumber: lap.lapNumber,
    points: lap.dataPoints.map((p) => ({
      distance: p.distance,
      speed: p.speed,
      lat: p.lat,
      lng: p.lng,
    })),
  }))
}
